import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Users, UserPlus, Edit, X, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const UserManagement = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        name: '',
        roleKey: 'installer',
        phone: '',
        isActive: true,
        // OEM specific fields
        oemData: {
            companyName: '',
            contactPerson: '',
            address: '',
            billingAddress: '',
            gstNumber: '',
            panNumber: '',
            logoUrl: ''
        },
        // Employee specific fields
        employeeData: {
            designation: '',
            department: '',
            joiningDate: '',
            monthlySalary: '',
            employmentType: 'full_time',
            skillLevel: 'beginner',
            yearsOfExperience: 0,
            certifications: '',
            specialization: '',
            bankName: '',
            accountNumber: '',
            ifscCode: '',
            panNumber: '',
            aadharNumber: '',
            emergencyContactName: '',
            emergencyContactRelation: '',
            emergencyContactPhone: '',
            currentAddress: '',
            permanentAddress: '',
            city: '',
            state: '',
            pinCode: ''
        }
    });
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const pageSize = 10;

    const roles = [
        { key: 'admin', label: 'Administrator' },
        { key: 'oem', label: 'OEM Partner' },
        { key: 'supervisor', label: 'SuperAligner' },
        { key: 'installer', label: 'Aligner' },
        { key: 'junioraligner', label: 'Junior Aligner' }
    ];

    useEffect(() => {
        fetchUsers();
    }, [currentPage]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await api.getUsers(currentPage, pageSize);
            setUsers(response.users);
            setTotalCount(response.totalCount);
            setTotalPages(response.totalPages);
        } catch (error) {
            toast({
                title: 'Error',
                description: error.message || 'Failed to fetch users',
                variant: 'destructive'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const userData = {
                email: formData.email || null,
                password: formData.password,
                name: formData.name,
                roleKey: formData.roleKey,
                phone: formData.phone || null
            };

            // Add OEM-specific data if role is 'oem'
            if (formData.roleKey === 'oem') {
                userData.oemData = {
                    companyName: formData.oemData.companyName,
                    contactPerson: formData.oemData.contactPerson,
                    address: formData.oemData.address || null,
                    billingAddress: formData.oemData.billingAddress || null,
                    gstNumber: formData.oemData.gstNumber || null,
                    panNumber: formData.oemData.panNumber || null,
                    logoUrl: formData.oemData.logoUrl || null
                };
            }

            // Add Employee-specific data if role is employee group
            if (['supervisor', 'installer', 'junioraligner'].includes(formData.roleKey)) {
                userData.employeeData = {
                    designation: formData.employeeData.designation || null,
                    department: formData.employeeData.department || null,
                    joiningDate: formData.employeeData.joiningDate || null,
                    monthlySalary: formData.employeeData.monthlySalary ? parseFloat(formData.employeeData.monthlySalary) : null,
                    employmentType: formData.employeeData.employmentType || 'full_time',
                    skillLevel: formData.employeeData.skillLevel || 'beginner',
                    yearsOfExperience: parseInt(formData.employeeData.yearsOfExperience) || 0,
                    certifications: formData.employeeData.certifications || null,
                    specialization: formData.employeeData.specialization || null,
                    bankName: formData.employeeData.bankName || null,
                    accountNumber: formData.employeeData.accountNumber || null,
                    ifscCode: formData.employeeData.ifscCode || null,
                    panNumber: formData.employeeData.panNumber || null,
                    aadharNumber: formData.employeeData.aadharNumber || null,
                    emergencyContactName: formData.employeeData.emergencyContactName || null,
                    emergencyContactRelation: formData.employeeData.emergencyContactRelation || null,
                    emergencyContactPhone: formData.employeeData.emergencyContactPhone || null,
                    currentAddress: formData.employeeData.currentAddress || null,
                    permanentAddress: formData.employeeData.permanentAddress || null,
                    city: formData.employeeData.city || null,
                    state: formData.employeeData.state || null,
                    pinCode: formData.employeeData.pinCode || null
                };
            }

            await api.createUser(userData);

            toast({
                title: 'Success',
                description: 'User created successfully'
            });

            setShowAddModal(false);
            setFormData({
                email: '',
                password: '',
                name: '',
                roleKey: 'installer',
                phone: '',
                isActive: true,
                oemData: {
                    companyName: '',
                    contactPerson: '',
                    address: '',
                    billingAddress: '',
                    gstNumber: '',
                    panNumber: '',
                    logoUrl: ''
                },
                employeeData: {
                    designation: '',
                    department: '',
                    joiningDate: '',
                    monthlySalary: '',
                    employmentType: 'full_time',
                    skillLevel: 'beginner',
                    yearsOfExperience: 0,
                    certifications: '',
                    specialization: '',
                    bankName: '',
                    accountNumber: '',
                    ifscCode: '',
                    panNumber: '',
                    aadharNumber: '',
                    emergencyContactName: '',
                    emergencyContactRelation: '',
                    emergencyContactPhone: '',
                    currentAddress: '',
                    permanentAddress: '',
                    city: '',
                    state: '',
                    pinCode: ''
                }
            });
            fetchUsers();
        } catch (error) {
            toast({
                title: 'Error',
                description: error.message || 'Failed to create user',
                variant: 'destructive'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditUser = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const updateData = {
                name: formData.name,
                phone: formData.phone || null,
                isActive: formData.isActive
            };

            // Only include password if provided
            if (formData.password && formData.password.trim()) {
                updateData.password = formData.password;
            }

            await api.updateUser(selectedUser.id, updateData);

            toast({
                title: 'Success',
                description: 'User updated successfully'
            });

            closeEditModal();
            fetchUsers();
        } catch (error) {
            toast({
                title: 'Error',
                description: error.message || 'Failed to update user',
                variant: 'destructive'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const openEditModal = (user) => {
        setSelectedUser(user);
        setFormData({
            email: user.email,
            name: user.name,
            roleKey: user.role,
            phone: user.phone || '',
            password: '',
            isActive: user.isActive
        });
        setShowPassword(false);
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setSelectedUser(null);
        setShowPassword(false);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/admin/settings')}
                        className="mb-4"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Settings
                    </Button>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shrink-0">
                                <Users className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">User Management</h1>
                                <p className="text-gray-600 text-sm sm:text-base">Total {totalCount} users</p>
                            </div>
                        </div>

                        <Button
                            className="bg-blue-600 hover:bg-blue-700 text-white self-start sm:self-auto"
                            onClick={() => navigate('/admin/settings/users/create')}
                        >
                            <UserPlus className="mr-2 h-4 w-4" />
                            Add New User
                        </Button>
                    </div>
                </div>

                {/* Users Table */}
                <Card>
                    <CardHeader>
                        <CardTitle>All Users</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex justify-center items-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                            </div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[900px]">
                                        <thead className="bg-gray-50 border-b">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Name</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Email</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Role</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Phone</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Status</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Created</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {users.map((user) => (
                                                <tr key={user.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{user.name}</td>
                                                    <td className="px-4 py-4 text-sm text-gray-600">{user.email}</td>
                                                    <td className="px-4 py-4">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                                                            user.role === 'oem' ? 'bg-blue-100 text-blue-800' :
                                                                user.role === 'supervisor' ? 'bg-green-100 text-green-800' :
                                                                    'bg-gray-100 text-gray-800'
                                                            }`}>
                                                            {roles.find(r => r.key === user.role)?.label || user.role}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4 text-sm text-gray-600">{user.phone || '-'}</td>
                                                    <td className="px-4 py-4">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                            }`}>
                                                            {user.isActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4 text-sm text-gray-600">{formatDate(user.createdAt)}</td>
                                                    <td className="px-4 py-4">
                                                        <div className="flex items-center gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => navigate(`/admin/settings/users/view/${user.id}`)}
                                                                title="View Details"
                                                            >
                                                                <Eye className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => navigate(`/admin/settings/users/edit/${user.id}`)}
                                                                title="Edit User"
                                                            >
                                                                <Edit className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                                    <div className="text-sm text-gray-600">
                                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} users
                                    </div>
                                    <div className="flex space-x-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                        >
                                            Previous
                                        </Button>
                                        <div className="flex items-center space-x-2">
                                            {[...Array(totalPages)].map((_, idx) => {
                                                const page = idx + 1;
                                                const showPage = page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2);
                                                const showEllipsis = page === 2 && currentPage > 4;
                                                const showEndEllipsis = page === totalPages - 1 && currentPage < totalPages - 3;

                                                if (!showPage && !showEllipsis && !showEndEllipsis) return null;

                                                if (showEllipsis || showEndEllipsis) {
                                                    return <span key={page} className="px-2">...</span>;
                                                }

                                                return (
                                                    <Button
                                                        key={page}
                                                        variant={currentPage === page ? "default" : "outline"}
                                                        size="sm"
                                                        onClick={() => setCurrentPage(page)}
                                                        className={currentPage === page ? "bg-blue-600" : ""}
                                                    >
                                                        {page}
                                                    </Button>
                                                );
                                            })}
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Add User Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
                    <Card className="w-full max-w-md mx-auto my-8">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>Add New User</CardTitle>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowAddModal(false)}
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleAddUser} className="space-y-4">
                                <div>
                                    <Label htmlFor="name">Full Name *</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="email">Email *</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        required
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="password">Password *</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        required
                                        minLength={6}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="role">Role *</Label>
                                    <select
                                        id="role"
                                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                                        value={formData.roleKey}
                                        onChange={(e) => setFormData({ ...formData, roleKey: e.target.value })}
                                    >
                                        {roles.map(role => (
                                            <option key={role.key} value={role.key}>{role.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <Label htmlFor="phone">Phone</Label>
                                    <Input
                                        id="phone"
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    />
                                </div>

                                {/* OEM-specific fields */}
                                {formData.roleKey === 'oem' && (
                                    <div className="border-t pt-4 mt-4">
                                        <h3 className="text-sm font-semibold mb-3 text-gray-700">Company Details</h3>
                                        <div className="space-y-3">
                                            <div>
                                                <Label htmlFor="companyName">Company Name *</Label>
                                                <Input
                                                    id="companyName"
                                                    value={formData.oemData.companyName}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        oemData: { ...formData.oemData, companyName: e.target.value }
                                                    })}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="contactPerson">Contact Person *</Label>
                                                <Input
                                                    id="contactPerson"
                                                    value={formData.oemData.contactPerson}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        oemData: { ...formData.oemData, contactPerson: e.target.value }
                                                    })}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="gstNumber">GST Number</Label>
                                                <Input
                                                    id="gstNumber"
                                                    value={formData.oemData.gstNumber}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        oemData: { ...formData.oemData, gstNumber: e.target.value }
                                                    })}
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="oemPanNumber">PAN Number</Label>
                                                <Input
                                                    id="oemPanNumber"
                                                    value={formData.oemData.panNumber}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        oemData: { ...formData.oemData, panNumber: e.target.value }
                                                    })}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Employee-specific fields */}
                                {['supervisor', 'installer', 'junioraligner'].includes(formData.roleKey) && (
                                    <div className="border-t pt-4 mt-4">
                                        <h3 className="text-sm font-semibold mb-3 text-gray-700">Employee Details</h3>
                                        <div className="space-y-3">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <div>
                                                    <Label htmlFor="designation">Designation</Label>
                                                    <Input
                                                        id="designation"
                                                        value={formData.employeeData.designation}
                                                        onChange={(e) => setFormData({
                                                            ...formData,
                                                            employeeData: { ...formData.employeeData, designation: e.target.value }
                                                        })}
                                                    />
                                                </div>
                                                <div>
                                                    <Label htmlFor="department">Department</Label>
                                                    <Input
                                                        id="department"
                                                        value={formData.employeeData.department}
                                                        onChange={(e) => setFormData({
                                                            ...formData,
                                                            employeeData: { ...formData.employeeData, department: e.target.value }
                                                        })}
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <div>
                                                    <Label htmlFor="joiningDate">Joining Date</Label>
                                                    <Input
                                                        id="joiningDate"
                                                        type="date"
                                                        value={formData.employeeData.joiningDate}
                                                        onChange={(e) => setFormData({
                                                            ...formData,
                                                            employeeData: { ...formData.employeeData, joiningDate: e.target.value }
                                                        })}
                                                    />
                                                </div>
                                                <div>
                                                    <Label htmlFor="monthlySalary">Monthly Salary</Label>
                                                    <Input
                                                        id="monthlySalary"
                                                        type="number"
                                                        value={formData.employeeData.monthlySalary}
                                                        onChange={(e) => setFormData({
                                                            ...formData,
                                                            employeeData: { ...formData.employeeData, monthlySalary: e.target.value }
                                                        })}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <Label htmlFor="empPanNumber">PAN Number</Label>
                                                <Input
                                                    id="empPanNumber"
                                                    value={formData.employeeData.panNumber}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        employeeData: { ...formData.employeeData, panNumber: e.target.value }
                                                    })}
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="aadharNumber">Aadhar Number</Label>
                                                <Input
                                                    id="aadharNumber"
                                                    value={formData.employeeData.aadharNumber}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        employeeData: { ...formData.employeeData, aadharNumber: e.target.value }
                                                    })}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-end space-x-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setShowAddModal(false)}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="bg-blue-600 hover:bg-blue-700"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Creating...
                                            </>
                                        ) : (
                                            'Create User'
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Edit User Modal */}
            {showEditModal && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
                    <Card className="w-full max-w-md mx-auto my-8">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>Edit User</CardTitle>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={closeEditModal}
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleEditUser} className="space-y-4">
                                <div>
                                    <Label htmlFor="edit-name">Full Name *</Label>
                                    <Input
                                        id="edit-name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="edit-email">Email</Label>
                                    <Input
                                        id="edit-email"
                                        value={formData.email}
                                        disabled
                                        className="bg-gray-100"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                                </div>
                                <div>
                                    <Label htmlFor="edit-role">Role</Label>
                                    <Input
                                        id="edit-role"
                                        value={roles.find(r => r.key === formData.roleKey)?.label || formData.roleKey}
                                        disabled
                                        className="bg-gray-100"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Role cannot be changed</p>
                                </div>
                                <div>
                                    <Label htmlFor="edit-phone">Phone</Label>
                                    <Input
                                        id="edit-phone"
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="edit-password">Password</Label>
                                    <div className="relative">
                                        <Input
                                            id="edit-password"
                                            type={showPassword ? "text" : "password"}
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            placeholder="••••••••"
                                            className="pr-10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">Click to edit and change password</p>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="edit-active"
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                        className="rounded"
                                    />
                                    <Label htmlFor="edit-active" className="cursor-pointer">Active</Label>
                                </div>
                                <div className="flex justify-end space-x-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={closeEditModal}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="bg-blue-600 hover:bg-blue-700"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Updating...
                                            </>
                                        ) : (
                                            'Update User'
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
